import { Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  BookingFlag,
  PresenceMonitoringData,
  PresenceMonitoringEmployee,
  PresenceMonitoringSettings,
} from 'src/shared/models/multiuser-monitoring-employee/multiuser-employee';
import { AuthenticatedHttpService } from './authenticatedHttpService.service';
import { HttpUtils } from 'src/shared/utils/http-utils';
import { environment } from 'src/environments/environment';

export interface MonitoringFilter {
  sortBy: MultiuserMonitoringSorting;
  byDepartmentId: number;
}

export enum MultiuserMonitoringSorting {
  EmployeeName = 1,
  PersonId,
  Departments,
  Status,
  DepartmentId,
  MonitorMode,
  None,
}

export enum StatisticPages {
  AllEmployees = 1,
  Present,
  Break,
  NotPresentOrSheduled,
}

export interface MonitoringEmployeeListData {
  employeeList: PresenceMonitoringEmployee[];
  filter: MonitoringFilter;
}

@Injectable({ providedIn: 'root' })
export class MonitoringService {
  private readonly _monitoringEmployeeList = signal<MonitoringEmployeeListData | null>(null);
  public readonly employeeList = this._monitoringEmployeeList.asReadonly();
  public readonly employeeList$ = toObservable(this._monitoringEmployeeList);

  private readonly _monitoringSettings = signal<PresenceMonitoringSettings | null>(null);
  public readonly monitoringSettings = this._monitoringSettings.asReadonly();
  public readonly monitoringSettings$ = toObservable(this._monitoringSettings);

  get defaultFilter() {
    return { byDepartmentId: -1, sortBy: MultiuserMonitoringSorting.None };
  }

  constructor(private http: AuthenticatedHttpService) {}

  async updateMonitoringEmployees(filter: MonitoringFilter = this.defaultFilter) {
    try {
      const presenceMonitoring = await HttpUtils.handle<PresenceMonitoringData>({
        request: this.http.get(`${environment.web.apiUrl}multiuser/presence-monitoring`),
      });

      if (
        presenceMonitoring &&
        presenceMonitoring.employeeList &&
        presenceMonitoring.employeeList.length > 0
      ) {
        const newEmployeeList = this.updateDuplicateDepartmentNames(
          presenceMonitoring.employeeList
        );
        this._monitoringEmployeeList.set({ employeeList: newEmployeeList, filter: filter });
      } else {
        this._monitoringEmployeeList.set({ employeeList: [], filter: filter });
      }

      if (presenceMonitoring) {
        this._monitoringSettings.set(presenceMonitoring.settings);
      }
    } catch (error) {
      this._monitoringEmployeeList.set({ employeeList: [], filter: filter });
      console.error(error);
    }
  }

  clearMonitoringEmployees() {
    this._monitoringEmployeeList.set({ employeeList: [], filter: this.defaultFilter });
    this._monitoringSettings.set(null);
  }

  async read() {
    return new Promise(async (resolve) => {
      try {
        await this.http.post(`${environment.web.identity}/api/monitoring/read`, null);

        resolve(true);
      } catch (e) {
        console.error(e);
        resolve(true);
      }
    });
  }

  getMonitoringEmployeesForPage(page: StatisticPages) {
    const employeeList = this._monitoringEmployeeList()?.employeeList;
    switch (page) {
      case StatisticPages.AllEmployees:
        return employeeList;

      case StatisticPages.Present:
        return employeeList?.filter(
          (e) =>
            e.bookingFlag === BookingFlag.Status_Correct_booking ||
            e.bookingFlag === BookingFlag.Status_Arrived_early ||
            e.bookingFlag === BookingFlag.Status_Arrived_late
        );

      case StatisticPages.Break:
        return employeeList?.filter((e) => e.bookingFlag === BookingFlag.Status_Break);

      case StatisticPages.NotPresentOrSheduled:
        return employeeList?.filter(
          (e) =>
            e.bookingFlag === BookingFlag.Status_Booking_missing ||
            e.bookingFlag === BookingFlag.Status_Not_present_or_scheduled ||
            e.bookingFlag === BookingFlag.Status_Left_early ||
            e.bookingFlag === BookingFlag.Status_Left_late ||
            e.bookingFlag === BookingFlag.Status_Absent_with_reason
        );
    }
  }

  getMonitoringGroupNames(): { name: string; id: number; increment: number | null }[] {
    return this._monitoringEmployeeList()!
      .employeeList.map((item) => ({
        name: item.departmentName,
        id: item.departmentId,
        increment: null,
      }))
      .filter((value, index, self) => self.map((x) => x.id).indexOf(value.id) === index)
      .sort((s1, s2) => s1.name.localeCompare(s2.name));
  }

  private updateDuplicateDepartmentNames(
    employeeList: PresenceMonitoringEmployee[]
  ): PresenceMonitoringEmployee[] {
    const getDepartmentNameById = (id: any) =>
      employeeList.filter((e) => e.departmentId == id)[0].departmentName;

    const groupByDepartmentId = Array.from(
      this.groupByProperty(employeeList, (employee) => employee.departmentId)
    ).map((group) => ({
      newDepartmentName: getDepartmentNameById(group[0]),
      employeeList: group[1],
      haveDuplicateName: false,
    }));

    for (let i = 0; i < groupByDepartmentId.length; i++) {
      const element1 = groupByDepartmentId[i];
      let increment = 2;

      for (let j = 0; j < groupByDepartmentId.length; j++) {
        const element2 = groupByDepartmentId[j];

        if (element1.newDepartmentName === element2.newDepartmentName && i !== j) {
          if (!element1.haveDuplicateName) element1.newDepartmentName += ' (1)';
          element2.newDepartmentName += ' (' + increment + ')';
          increment++;
        }
      }
    }

    const newEmployeeDepartmentNames = groupByDepartmentId.map((group) => {
      const newEmployeeList: PresenceMonitoringEmployee[] = [];

      group.employeeList.forEach((el) => {
        const newEmployee = el;
        newEmployee.departmentName = group.newDepartmentName;
        newEmployeeList.push(newEmployee);
      });

      return newEmployeeList;
    });

    return newEmployeeDepartmentNames.flat();
  }

  private groupByProperty(
    list: PresenceMonitoringEmployee[],
    keyGetter: (s: PresenceMonitoringEmployee) => {},
    enumType = null
  ): Map<string, PresenceMonitoringEmployee[]> {
    const map = new Map();

    list.forEach((item) => {
      let key = keyGetter(item);
      if (enumType !== null) {
        key = enumType[key as number];
      }

      const collection = map.get(key);

      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });

    return map;
  }
}
